import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Heart, ExternalLink } from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setPosts(res.data || []);
      } catch (err) {
        console.error("Failed to fetch gallery:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, []);

  return (
    <section id="instagram" data-testid="instagram-section" className="py-20 md:py-32 bg-white relative overflow-hidden">
      <div className="bubble-decoration bg-pink-400 w-56 h-56 -top-28 right-20 animate-pulse-soft" />

      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-4"
        >
          <div>
            <span className="font-accent text-2xl text-amber-600">Gallery</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900 mt-2">
              From Our <span className="text-red-600">Instagram</span>
            </h2>
            <p className="text-base text-stone-600 mt-3 max-w-lg">
              Catch a glimpse of our services, events and community life.
            </p>
          </div>
          <a
            href="https://www.instagram.com/winnerschapel_ipswich/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-btn"
            className="flex items-center gap-2 bg-gradient-to-r from-pink-500 via-red-500 to-amber-500 text-white rounded-full px-6 py-3 font-semibold text-sm hover:shadow-lg transition-transform duration-200 hover:-translate-y-1 shrink-0 w-fit"
          >
            <FaInstagram size={18} />
            Follow Us
          </a>
        </motion.div>

        {/* Grid */}
        {loading ? (
          <div className="instagram-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-stone-100 h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="instagram-grid">
            {posts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group relative rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-stone-100 cursor-pointer bubbly-card"
                data-testid={`instagram-post-${post.id}`}
              >
                <img
                  src={post.image_url}
                  alt={post.caption}
                  className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                  style={post.object_position ? { objectPosition: post.object_position } : undefined}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                  <p className="text-white text-sm line-clamp-2 mb-2">{post.caption}</p>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-white/90 text-xs">
                      <Heart size={14} fill="white" /> {post.likes}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
